import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import api from '../services/api';
import { toast } from 'sonner';
import {
  TrendingDown,
  TrendingUp,
  AlertTriangle,
  Download,
  RefreshCw,
  Phone,
  Mail,
  MessageSquare,
  Mic,
  Building2,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Plus,
  Pencil,
  X,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const fmt = (n, decimals = 2) =>
  n == null ? '—' : Number(n).toFixed(decimals) + ' €';
const fmtPct = (n) => (n == null ? '—' : Number(n).toFixed(1) + '%');
const fmtNum = (n) => (n == null ? '—' : String(n));

const PLANS = ['BASIC', 'EXPERT', 'PREMIUM'];
const PLAN_COLORS = { BASIC: 'zinc', EXPERT: 'orange', PREMIUM: 'green' };
const STATUS_COLORS = { ACTIVE: 'green', TRIAL: 'orange', CANCELLED: 'red', NONE: 'zinc' };

function SummaryCard({ label, value, sub, accent }) {
  const colors = {
    green: 'border-green-500/30 text-green-400',
    red: 'border-red-500/30 text-red-400',
    orange: 'border-orange-500/30 text-orange-400',
    zinc: 'border-zinc-700 text-zinc-300',
  };
  return (
    <div className={`rounded-xl border bg-zinc-900 p-5 ${colors[accent] || colors.zinc}`}>
      <p className="text-xs text-zinc-500 mb-1">{label}</p>
      <p className={`text-2xl font-semibold ${colors[accent]?.split(' ')[1] || 'text-zinc-100'}`}>
        {value}
      </p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

function Badge({ children, color }) {
  const map = {
    red: 'bg-red-500/10 text-red-400 border border-red-500/30',
    orange: 'bg-orange-500/10 text-orange-400 border border-orange-500/30',
    yellow: 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30',
    green: 'bg-green-500/10 text-green-400 border border-green-500/30',
    zinc: 'bg-zinc-800 text-zinc-400',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${map[color] || map.zinc}`}>
      {children}
    </span>
  );
}

function MissingData() {
  return <span className="text-zinc-600 text-xs italic">Sin datos</span>;
}

function CenterRow({ center }) {
  const [expanded, setExpanded] = useState(false);
  const v = center.vapi;
  const t = center.telnyx;
  const w = center.whatsapp;
  const r = center.revenue;

  const vapiPct = v.minutesIncluded > 0 ? (v.minutesThisMonth / v.minutesIncluded) * 100 : 0;
  const marginNeg = center.marginEur < 0;
  const vapiAlert = vapiPct >= 80;

  return (
    <>
      <tr
        className={`border-b border-zinc-800 hover:bg-zinc-800/40 cursor-pointer transition-colors ${
          marginNeg ? 'bg-red-500/5' : ''
        }`}
        onClick={() => setExpanded((x) => !x)}
      >
        <td className="px-4 py-3 text-sm font-medium text-zinc-100 whitespace-nowrap">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
            {center.centerName || center.centerId}
          </div>
        </td>
        <td className="px-4 py-3 text-sm whitespace-nowrap">
          {r.plan ? (
            <Badge color={PLAN_COLORS[r.plan] || 'zinc'}>{r.plan}</Badge>
          ) : (
            <span className="text-zinc-600 text-xs">—</span>
          )}
        </td>
        <td className="px-4 py-3 text-sm text-zinc-300 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            {vapiAlert && <AlertTriangle className="w-3.5 h-3.5 text-orange-400" />}
            {v.minutesThisMonth.toFixed(1)} min
            {v.minutesIncluded > 0 && (
              <span className="text-zinc-600 text-xs">/ {v.minutesIncluded}</span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-zinc-300 whitespace-nowrap">
          <div className="flex items-center gap-1">
            {fmt(v.estimatedCostEur)}
            {v.dataSource === 'real' && <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" title="Datos reales VAPI" />}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-zinc-300 whitespace-nowrap">{fmt(t.estimatedCostEur)}</td>
        <td className="px-4 py-3 text-sm whitespace-nowrap">
          {w.missingData ? (
            <MissingData />
          ) : w.creditsRemaining === 0 ? (
            <Badge color="yellow">Sin créditos</Badge>
          ) : w.creditsRemaining != null ? (
            <span className="text-zinc-300 text-sm">{w.creditsRemaining} créditos</span>
          ) : (
            <span className="text-zinc-400 text-xs">Ilimitado</span>
          )}
        </td>
        <td className="px-4 py-3 text-sm font-medium text-zinc-100 whitespace-nowrap">{fmt(r.mrrEur)}</td>
        <td className="px-4 py-3 text-sm font-medium whitespace-nowrap">
          <span className={marginNeg ? 'text-red-400' : 'text-green-400'}>{fmt(center.variableCostEur)}</span>
        </td>
        <td className="px-4 py-3 text-sm font-semibold whitespace-nowrap">
          <div className="flex items-center gap-1">
            {marginNeg ? <TrendingDown className="w-3.5 h-3.5 text-red-400" /> : <TrendingUp className="w-3.5 h-3.5 text-green-400" />}
            <span className={marginNeg ? 'text-red-400' : 'text-green-400'}>{fmt(center.marginEur)}</span>
            <span className={`text-xs ${marginNeg ? 'text-red-500' : 'text-green-500'}`}>
              ({fmtPct(center.marginPct)})
            </span>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-zinc-900/60">
          <td colSpan={9} className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
              {/* VAPI */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-zinc-400 font-medium mb-2">
                  <Mic className="w-3.5 h-3.5" /> Voz IA (VAPI)
                  {v.dataSource === 'real'
                    ? <Badge color="green">Datos reales</Badge>
                    : <Badge color="zinc">Estimado</Badge>
                  }
                </div>
                <div className="flex justify-between"><span className="text-zinc-500">Llamadas este mes</span><span className="text-zinc-300">{fmtNum(v.callsCount)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Minutos usados</span><span className="text-zinc-300">{v.minutesThisMonth.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Minutos incluidos</span><span className="text-zinc-300">{fmtNum(v.minutesIncluded)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Excedidos</span><span className={v.minutesExceeded > 0 ? 'text-orange-400' : 'text-zinc-300'}>{v.minutesExceeded.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Límite alcanzado</span><span className={v.limitReached ? 'text-red-400' : 'text-zinc-300'}>{v.limitReached ? 'Sí' : 'No'}</span></div>
                <div className="flex justify-between font-medium">
                  <span className="text-zinc-400">{v.dataSource === 'real' ? 'Coste real' : 'Coste estimado'}</span>
                  <span className="text-zinc-200">
                    {fmt(v.estimatedCostEur)}
                    {v.costUsd != null && <span className="text-zinc-500 ml-1">(${Number(v.costUsd).toFixed(4)})</span>}
                  </span>
                </div>
                {v.syncedAt && <p className="text-zinc-600 text-[10px]">Sync: {new Date(v.syncedAt).toLocaleString()}</p>}
                {vapiPct > 0 && (
                  <div className="mt-1.5">
                    <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${vapiPct >= 100 ? 'bg-red-500' : vapiPct >= 80 ? 'bg-orange-500' : 'bg-green-500'}`} style={{ width: `${Math.min(100, vapiPct)}%` }} />
                    </div>
                    <span className="text-zinc-600 text-[10px]">{vapiPct.toFixed(0)}% consumido</span>
                  </div>
                )}
              </div>

              {/* Telnyx */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-zinc-400 font-medium mb-2">
                  <Phone className="w-3.5 h-3.5" /> Telefonía (Telnyx)
                  {t.dataSource === 'real'
                    ? <Badge color="green">Datos reales</Badge>
                    : <Badge color="zinc">Estimado</Badge>
                  }
                </div>
                <div className="flex justify-between"><span className="text-zinc-500">Número asignado</span><span className="text-zinc-300">{t.hasNumber ? 'Sí' : 'No'}</span></div>
                {t.phoneNumber && <div className="flex justify-between"><span className="text-zinc-500">Número</span><span className="text-zinc-300 font-mono text-[11px]">{t.phoneNumber}</span></div>}
                <div className="flex justify-between"><span className="text-zinc-500">Coste número/mes</span><span className="text-zinc-300">{fmt(t.numberMonthlyCostEur)}</span></div>
                {t.dataSource === 'real' ? (
                  <>
                    <div className="flex justify-between"><span className="text-zinc-500">Llamadas entrantes</span><span className="text-zinc-300">{fmtNum(t.inboundCallsCount)}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Llamadas salientes</span><span className="text-zinc-300">{fmtNum(t.outboundCallsCount)}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Min reales</span><span className="text-zinc-300">{t.minutesEstimated?.toFixed(2)}</span></div>
                  </>
                ) : (
                  <div className="flex justify-between"><span className="text-zinc-500">Min estimados</span><span className="text-zinc-300">{t.minutesEstimated?.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-zinc-500">Coste llamadas</span><span className="text-zinc-300">{fmt(t.callCostEur)}</span></div>
                <div className="flex justify-between font-medium">
                  <span className="text-zinc-400">{t.dataSource === 'real' ? 'Coste real' : 'Coste estimado'}</span>
                  <span className="text-zinc-200">{fmt(t.estimatedCostEur)}</span>
                </div>
                {t.syncedAt && <p className="text-zinc-600 text-[10px]">Sync: {new Date(t.syncedAt).toLocaleString()}</p>}
              </div>

              {/* WhatsApp */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-zinc-400 font-medium mb-2">
                  <MessageSquare className="w-3.5 h-3.5" /> WhatsApp
                </div>
                <div className="flex justify-between"><span className="text-zinc-500">Configurado</span><span className="text-zinc-300">{w.hasWhatsApp ? 'Sí' : 'No'}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Créditos restantes</span>
                  <span>{w.creditsRemaining == null ? <MissingData /> : w.creditsRemaining === 0 ? <Badge color="yellow">0</Badge> : w.creditsRemaining}</span>
                </div>
                <div className="flex justify-between"><span className="text-zinc-500">Enviados este mes</span><span><MissingData /></span></div>
                <p className="text-zinc-600 text-[10px] leading-tight mt-1">El contador de mensajes enviados se almacena en Dentalbot (SQLite), no en MongoDB.</p>
              </div>

              {/* Revenue / Subscription */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-zinc-400 font-medium mb-2">
                  <CreditCard className="w-3.5 h-3.5" /> Suscripción AdamSched
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Plan</span>
                  <span>{r.plan ? <Badge color={PLAN_COLORS[r.plan] || 'zinc'}>{r.plan}</Badge> : <span className="text-zinc-600">—</span>}</span>
                </div>
                <div className="flex justify-between"><span className="text-zinc-500">Precio plan</span><span className="text-zinc-300">{fmt(r.planPriceEur)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Add-ons</span><span className="text-zinc-300">{fmt(r.addonTotalEur)}</span></div>
                <div className="flex justify-between font-medium"><span className="text-zinc-400">MRR</span><span className="text-zinc-200">{fmt(r.mrrEur)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Estado</span>
                  <Badge color={STATUS_COLORS[r.subscriptionStatus] || 'zinc'}>{r.subscriptionStatus}</Badge>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

const ADDON_IDS = ['AI_MINUTES_200', 'WA_REMINDERS_400', 'CAMPAIGNS_50'];
const ADDON_LABELS = {
  AI_MINUTES_200: '+200 min IA',
  WA_REMINDERS_400: '+400 WA',
  CAMPAIGNS_50: '+50 campañas',
};

function SubscriptionModal({ sub, centers, subscriptions, onClose, onSaved }) {
  const { t } = useTranslation();
  const isNew = !sub;
  const existingCenterIds = new Set(subscriptions.map((s) => s.centerId));

  const [form, setForm] = useState({
    centerId: sub?.centerId || '',
    plan: sub?.plan || 'BASIC',
    status: sub?.status || 'ACTIVE',
    notes: sub?.notes || '',
    addons: sub?.addons || [],
  });
  const [saving, setSaving] = useState(false);

  const availableCenters = centers.filter(
    (c) => isNew ? !existingCenterIds.has(c.id || c.centerId) : true
  );

  const toggleAddon = (addonId) => {
    setForm((f) => {
      const exists = f.addons.find((a) => a.addonId === addonId);
      if (exists) {
        return { ...f, addons: f.addons.filter((a) => a.addonId !== addonId) };
      }
      return { ...f, addons: [...f.addons, { addonId, quantity: 1 }] };
    });
  };

  const handleSave = async () => {
    if (!form.centerId || !form.plan) return;
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/admin/subscriptions', form);
        toast.success(t('success.costDashboard.subscriptionCreated'));
      } else {
        await api.put(`/admin/subscriptions/${sub.centerId}`, {
          plan: form.plan,
          status: form.status,
          notes: form.notes,
          addons: form.addons,
        });
        toast.success(t('success.costDashboard.subscriptionUpdated'));
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('¿Cancelar la suscripción?')) return;
    setSaving(true);
    try {
      await api.delete(`/admin/subscriptions/${sub.centerId}`);
      toast.success(t('success.costDashboard.subscriptionCancelled'));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(t('success.costDashboard.cancelError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-md space-y-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-zinc-100">
            {isNew ? 'Nueva suscripción' : `Editar — ${sub.centerId}`}
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="w-5 h-5" /></button>
        </div>

        {isNew && (
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Centro</label>
            <select
              value={form.centerId}
              onChange={(e) => setForm((f) => ({ ...f, centerId: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200"
            >
              <option value="">Selecciona un centro</option>
              {availableCenters.map((c) => (
                <option key={c.id || c.centerId} value={c.id || c.centerId}>
                  {c.name || c.centerName || c.id || c.centerId}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-xs text-zinc-500 mb-1">Plan</label>
          <div className="flex gap-2">
            {PLANS.map((p) => (
              <button
                key={p}
                onClick={() => setForm((f) => ({ ...f, plan: p }))}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  form.plan === p
                    ? 'bg-zinc-100 text-zinc-900 border-zinc-100'
                    : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-500'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs text-zinc-500 mb-1">Add-ons</label>
          <div className="flex flex-wrap gap-2">
            {ADDON_IDS.map((id) => {
              const active = form.addons.some((a) => a.addonId === id);
              return (
                <button
                  key={id}
                  onClick={() => toggleAddon(id)}
                  className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                    active
                      ? 'bg-zinc-100 text-zinc-900 border-zinc-100'
                      : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-500'
                  }`}
                >
                  {ADDON_LABELS[id]}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-xs text-zinc-500 mb-1">Estado</label>
          <select
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200"
          >
            <option value="ACTIVE">ACTIVE</option>
            <option value="TRIAL">TRIAL</option>
            <option value="CANCELLED">CANCELLED</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-zinc-500 mb-1">Notas (interno)</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={2}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 resize-none"
          />
        </div>

        <div className="flex gap-2 pt-2">
          {!isNew && (
            <Button variant="outline" size="sm" onClick={handleCancel} disabled={saving} className="text-red-400 border-red-500/30 hover:bg-red-500/10">
              Cancelar suscripción
            </Button>
          )}
          <Button size="sm" onClick={handleSave} disabled={saving || !form.centerId || !form.plan} className="ml-auto">
            {saving ? 'Guardando...' : isNew ? 'Crear' : 'Guardar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function CostDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [allCenters, setAllCenters] = useState([]);
  const [modalSub, setModalSub] = useState(undefined); // undefined = closed, null = new, obj = edit
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [filterCenter, setFilterCenter] = useState('');

  useEffect(() => {
    if (user?.role !== 'SUPER_ADMIN') {
      navigate('/planner', { replace: true });
    }
  }, [user, navigate]);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const [subsRes, centersRes] = await Promise.all([
        api.get('/admin/subscriptions'),
        api.get('/admin/centers'),
      ]);
      setSubscriptions(subsRes.data || []);
      setAllCenters(centersRes.data || []);
    } catch {
      // non-fatal
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (filterCenter) params.center_id = filterCenter;
      const res = await api.get('/admin/cost-dashboard', { params });
      setData(res.data);
    } catch (e) {
      toast.error(t('success.costDashboard.loadError'));
    } finally {
      setLoading(false);
    }
  }, [month, filterCenter, t]);

  useEffect(() => {
    if (user?.role === 'SUPER_ADMIN') {
      fetchData();
      fetchSubscriptions();
    }
  }, [fetchData, fetchSubscriptions, user]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/cost-dashboard/export', {
        params: { month },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `adamsched-costes-${month}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('success.costDashboard.exportError'));
    } finally {
      setExporting(false);
    }
  };

  if (user?.role !== 'SUPER_ADMIN') return null;

  const s = data?.summary;
  const centers = data?.centers || [];
  const infra = data?.infrastructure;

  return (
    <div className="p-6 space-y-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-100">Dashboard de Costes</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Solo visible para SUPER_ADMIN</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 text-zinc-200 text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-zinc-500"
          />
          <select
            value={filterCenter}
            onChange={(e) => setFilterCenter(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 text-zinc-200 text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-zinc-500"
          >
            <option value="">Todos los centros</option>
            {centers.map((c) => (
              <option key={c.centerId} value={c.centerId}>{c.centerName || c.centerId}</option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="gap-1.5">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Cargando...' : 'Actualizar'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} className="gap-1.5">
            <Download className="w-4 h-4" />
            {exporting ? 'Exportando...' : 'Exportar Excel'}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
          <SummaryCard
            label="MRR"
            value={fmt(s.mrrEur)}
            sub={`${s.activeSubscriptions} suscripciones activas`}
            accent="green"
          />
          <SummaryCard
            label="ARR"
            value={fmt(s.arrEur)}
            sub="MRR × 12"
            accent="green"
          />
          <SummaryCard
            label="Coste variable"
            value={fmt(s.totalVariableCostEur)}
            sub={`${s.centersCount} centros`}
            accent="zinc"
          />
          <SummaryCard
            label="Margen"
            value={fmt(s.totalMarginEur)}
            sub={fmtPct(s.totalMarginPct)}
            accent={s.totalMarginEur < 0 ? 'red' : 'green'}
          />
          <SummaryCard
            label="Centros margen neg."
            value={String(s.centersWithNegativeMargin)}
            sub=""
            accent={s.centersWithNegativeMargin > 0 ? 'red' : 'zinc'}
          />
          <SummaryCard
            label="Alertas VAPI >80%"
            value={String(s.centersWithVapiAlert)}
            sub=""
            accent={s.centersWithVapiAlert > 0 ? 'orange' : 'zinc'}
          />
          <SummaryCard
            label="Sin suscripción"
            value={String(s.centersCount - s.activeSubscriptions)}
            sub="centros sin plan activo"
            accent={(s.centersCount - s.activeSubscriptions) > 0 ? 'orange' : 'zinc'}
          />
        </div>
      )}

      {/* Centers table */}
      <div className="rounded-xl border border-zinc-800 overflow-hidden">
        <div className="px-5 py-3 bg-zinc-900 border-b border-zinc-800 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-zinc-400" />
          <h2 className="text-sm font-medium text-zinc-200">Desglose por centro</h2>
          <span className="text-xs text-zinc-500 ml-1">— click en fila para expandir</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-900/50">
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">Centro</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">VAPI (min)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">VAPI €</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">Telnyx €</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">WhatsApp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">MRR €</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">Coste var.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">Margen</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-zinc-500">Cargando...</td></tr>
              ) : centers.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-zinc-500">Sin datos para este periodo</td></tr>
              ) : (
                centers.map((c) => <CenterRow key={c.centerId} center={c} />)
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Subscriptions manager */}
      <div className="rounded-xl border border-zinc-800 overflow-hidden">
        <div className="px-5 py-3 bg-zinc-900 border-b border-zinc-800 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-zinc-400" />
          <h2 className="text-sm font-medium text-zinc-200">Suscripciones AdamSched</h2>
          <span className="text-xs text-zinc-500 ml-auto">BASIC 39 € · EXPERT 59 € · PREMIUM 99 €</span>
          <Button size="sm" variant="outline" className="gap-1.5 ml-2" onClick={() => setModalSub(null)}>
            <Plus className="w-3.5 h-3.5" /> Nueva
          </Button>
        </div>
        {subscriptions.length === 0 ? (
          <p className="px-5 py-6 text-sm text-zinc-500">Sin suscripciones registradas.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800 bg-zinc-900/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">Centro</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">Add-ons</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">MRR €</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide">Inicio</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wide"></th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((sub) => (
                  <tr key={sub.centerId} className="border-b border-zinc-800 hover:bg-zinc-800/30">
                    <td className="px-4 py-3 text-zinc-200">{sub.centerId}</td>
                    <td className="px-4 py-3">
                      <Badge color={PLAN_COLORS[sub.plan] || 'zinc'}>{sub.plan}</Badge>
                    </td>
                    <td className="px-4 py-3 text-zinc-400">
                      {sub.addons?.length ? sub.addons.map((a) => ADDON_LABELS[a.addonId] || a.addonId).join(', ') : '—'}
                    </td>
                    <td className="px-4 py-3 text-zinc-200 font-medium">{fmt(sub.mrrEur)}</td>
                    <td className="px-4 py-3">
                      <Badge color={STATUS_COLORS[sub.status] || 'zinc'}>{sub.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">{sub.startDate || '—'}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => setModalSub(sub)} className="text-zinc-500 hover:text-zinc-200 transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Infrastructure */}
      {infra && (
        <div className="rounded-xl border border-zinc-800 overflow-hidden">
          <div className="px-5 py-3 bg-zinc-900 border-b border-zinc-800">
            <h2 className="text-sm font-medium text-zinc-200">Infraestructura compartida</h2>
            <p className="text-xs text-zinc-500 mt-0.5">Costes fijos no imputados por centro — configurables via variables de entorno</p>
          </div>
          <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: 'MongoDB Atlas', value: infra.mongoDB },
              { label: 'Render', value: infra.render },
              { label: 'Vercel', value: infra.vercel },
              { label: 'Dominio', value: infra.domain },
              { label: 'Brevo (plan)', value: infra.brevoPlan },
              { label: 'TOTAL', value: infra.totalEur, bold: true },
            ].map(({ label, value, bold }) => (
              <div key={label} className={`text-center p-3 rounded-lg bg-zinc-900 border border-zinc-800 ${bold ? 'border-zinc-600' : ''}`}>
                <p className="text-xs text-zinc-500 mb-1">{label}</p>
                <p className={`text-sm font-${bold ? 'semibold' : 'normal'} ${value === 0 ? 'text-zinc-600' : 'text-zinc-200'}`}>
                  {value === 0 ? 'No config.' : fmt(value)}
                </p>
              </div>
            ))}
          </div>
          <p className="px-5 pb-4 text-xs text-zinc-600">
            Configura los costes reales via env: COST_INFRA_MONGODB_MONTHLY_EUR, COST_INFRA_RENDER_MONTHLY_EUR, COST_INFRA_VERCEL_MONTHLY_EUR, COST_INFRA_DOMAIN_MONTHLY_EUR, COST_INFRA_BREVO_PLAN_MONTHLY_EUR
          </p>
        </div>
      )}

      {/* Rates reference */}
      {data?.costRates && (
        <div className="text-xs text-zinc-600 border border-zinc-800 rounded-lg p-4 space-y-1">
          <p className="text-zinc-500 font-medium mb-2">Tarifas aplicadas (configurables via env)</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
            <span>VAPI: {data.costRates.vapiPerMinuteEur} €/min</span>
            <span>Telnyx: {data.costRates.telnyxPerMinuteEur} €/min</span>
            <span>Telnyx número: {data.costRates.telnyxNumberMonthlyEur} €/mes</span>
            <span>WhatsApp utility: {data.costRates.whatsappUtilityEur} €/msg</span>
            <span>WhatsApp marketing: {data.costRates.whatsappMarketingEur} €/msg</span>
            <span>Brevo: {data.costRates.brevoPerEmailEur} €/email</span>
          </div>
        </div>
      )}

      {/* Subscription modal */}
      {modalSub !== undefined && (
        <SubscriptionModal
          sub={modalSub}
          centers={allCenters}
          subscriptions={subscriptions}
          onClose={() => setModalSub(undefined)}
          onSaved={() => { fetchSubscriptions(); fetchData(); }}
        />
      )}
    </div>
  );
}
